import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import HtmlEditor from "./HtmlEditor";

export default function BlogEditor({
  baseURL = "https://gantasoft.in/rankridgeschool/dev/v1",
  apiKey = "12345678",
  initial = {}, onClose, onSaved,
}) {
  const api = useMemo(()=>axios.create({ baseURL, headers: { "X-API-Key": apiKey } }), [baseURL, apiKey]);

  const [form,setForm] = useState({
    post_id: initial.post_id || null,
    author_id: initial.author_id || null,
    category_id: initial.category_id || null,
    slug: initial.slug || "",
    title: initial.title || "",
    excerpt: initial.excerpt || "",
    cover_image: initial.cover_image || "",
    body_html: initial.body_html || "",
    status: initial.status || "draft",
    published_at: initial.published_at || "",
    is_featured: initial.is_featured || 0,
    meta_title: initial.meta_title || "",
    meta_description: initial.meta_description || "",
    canonical_path: initial.canonical_path || "",
    og_image: initial.og_image || "",
    tag_ids: (initial.tags||[]).map(t=>t.tag_id) || [],
  });

  const [authors,setAuthors]=useState([]), [cats,setCats]=useState([]), [tags,setTags]=useState([]);
  const [file,setFile]=useState(null); const [uploading,setUploading]=useState(false);
  const [saving,setSaving]=useState(false); const [error,setError]=useState("");

  useEffect(()=>{ (async()=>{
    try {
      const [a,c,t] = await Promise.all([
        api.get("/authors").catch(()=>({data:{items:[]}})),     // iff you create authors.php
        api.get("/categories"),
        api.get("/tags"),
      ]);
      setAuthors(a?.data?.items||[]); setCats(c?.data?.items||[]); setTags(t?.data?.items||[]);
    } catch(e){ setError(e?.response?.data?.message || e.message); }
  })(); }, [api]);

  const upd = (k,v)=>setForm(f=>({...f,[k]:v}));

  const upload = async ()=>{
    if(!file) return;
    setUploading(true);
    try{
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dir", "blog");
      const {data} = await api.post("/upload", fd, { headers:{"Content-Type":"multipart/form-data"} });
      if (data?.success && data?.path) upd("cover_image", data.path);
    } finally { setUploading(false); }
  };

  const save = async ()=>{
    if (!form.slug.trim() || !form.title.trim() || !form.body_html.trim()) { setError("Slug, title & body are required."); return; }
    setSaving(true); setError("");
    const payload = { ...form, tag_ids: form.tag_ids };
    try{
      if (form.post_id) await api.put(`/posts/${form.post_id}`, payload);
      else {
        const {data} = await api.post("/posts", payload);
        upd("post_id", data?.post_id || null);
      }
      onSaved && onSaved();
    } catch(e){ setError(e?.response?.data?.message || e.message || "Save failed"); }
    finally { setSaving(false); }
  };

  // simple slugify helper
  const slugify = s => s.toLowerCase().trim()
     .replace(/[^a-z0-9\s-]/g,'').replace(/\s+/g,'-').replace(/-+/g,'-');

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[92vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-semibold">{form.post_id ? "Edit Post" : "New Post"}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        <div className="p-4 grid gap-4">
          {error && <div className="px-3 py-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded">{error}</div>}

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm">Title *</label>
              <input className="border rounded px-3 py-2 w-full" value={form.title} onChange={e=>{upd("title", e.target.value); if(!form.post_id){ upd("slug", slugify(e.target.value)); }}} />
            </div>
            <div>
              <label className="text-sm">Slug *</label>
              <input className="border rounded px-3 py-2 w-full" value={form.slug} onChange={e=>upd("slug", slugify(e.target.value))} />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm">Author</label>
              <select className="border rounded px-3 py-2 w-full" value={form.author_id||""} onChange={e=>upd("author_id", e.target.value?Number(e.target.value):null)}>
                <option value="">— Select —</option>
                {authors.map(a=><option value={a.author_id} key={a.author_id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm">Category</label>
              <select className="border rounded px-3 py-2 w-full" value={form.category_id||""} onChange={e=>upd("category_id", e.target.value?Number(e.target.value):null)}>
                <option value="">— Select —</option>
                {cats.map(c=><option value={c.category_id} key={c.category_id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm">Tags</label>
            <div className="flex flex-wrap gap-2">
              {tags.map(t=>{
                const active = form.tag_ids.includes(t.tag_id);
                return (
                  <button key={t.tag_id}
                    type="button"
                    onClick={()=>upd("tag_ids", active ? form.tag_ids.filter(id=>id!==t.tag_id) : [...form.tag_ids, t.tag_id])}
                    className={`text-xs px-2 py-1 rounded border ${active?'bg-gray-800 text-white':'bg-white text-gray-700'}`}>
                    #{t.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-sm">Excerpt</label>
            <textarea rows={3} className="border rounded px-3 py-2 w-full" value={form.excerpt} onChange={e=>upd("excerpt", e.target.value)} />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm">Cover Image URL</label>
              <input className="border rounded px-3 py-2 w-full" value={form.cover_image} onChange={e=>upd("cover_image", e.target.value)} />
              <div className="mt-2 flex items-center gap-2">
                <input type="file" accept="image/*" onChange={e=>setFile(e.target.files?.[0]||null)} />
                <button onClick={upload} disabled={!file||uploading} className="px-3 py-1.5 bg-gray-800 text-white rounded disabled:opacity-50">
                  {uploading?'Uploading…':'Upload'}
                </button>
              </div>
              {form.cover_image && <img src={"https://gantasoft.in/rankridgeschool/dev"+ form.cover_image} alt="" className="mt-2 rounded w-full h-40 object-cover" />}
            </div>
            <div>
              <label className="text-sm">Status / Publish At / Featured</label>
              <div className="grid grid-cols-3 gap-2">
                <select className="border rounded px-3 py-2" value={form.status} onChange={e=>upd("status", e.target.value)}>
                  <option value="draft">draft</option>
                  <option value="scheduled">scheduled</option>
                  <option value="published">published</option>
                  <option value="archived">archived</option>
                </select>
                <input type="datetime-local" className="border rounded px-3 py-2" value={form.published_at||""} onChange={e=>upd("published_at", e.target.value)} />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!form.is_featured} onChange={e=>upd("is_featured", e.target.checked?1:0)} />
                  Featured
                </label>
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm">Body (HTML) *</label>
            {/* <textarea rows={14} className="border rounded px-3 py-2 w-full font-mono" value={form.body_html} onChange={e=>upd("body_html", e.target.value)} placeholder="<p>Your HTML…</p>" /> */}
           
           <HtmlEditor
  value={form.body_html} 
  onChange={(html) => upd("body_html", html)} 
  minHeight={200} 
  uploadImage={upload} 
  className="rounded border"  // optional wrapper styling 
/>
           
            <p className="text-xs text-gray-500 mt-1">Tip: You can author in Markdown in your CMS and convert to HTML server-side, or paste clean HTML here.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm">Meta Title</label>
              <input className="border rounded px-3 py-2 w-full" value={form.meta_title} onChange={e=>upd("meta_title", e.target.value)} />
              <label className="text-sm mt-3 block">Meta Description</label>
              <textarea rows={3} className="border rounded px-3 py-2 w-full" value={form.meta_description} onChange={e=>upd("meta_description", e.target.value)} />
            </div>
            <div>
              <label className="text-sm">Canonical Path</label>
              <input className="border rounded px-3 py-2 w-full" value={form.canonical_path} onChange={e=>upd("canonical_path", e.target.value)} placeholder="/blog/my-post" />
              <label className="text-sm mt-3 block">OG Image</label>
              <input className="border rounded px-3 py-2 w-full" value={form.og_image} onChange={e=>upd("og_image", e.target.value)} />
            </div>
          </div>
        </div>

        <div className="p-4 border-t flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded bg-green-700 text-white disabled:opacity-60">
            {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="px-4 py-2 rounded bg-gray-200">Cancel</button>
        </div>
      </div>
    </div>
  );
}
